import { DealerContactType } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { StyledTextField, Title, useDealerDetailsContext } from '@components';
import { Box, Grid } from '@mui/material';
import { getDealerPublicAddress } from '@store/dealer/dealer.slice';
import { t } from '@translations';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';

export const DealerDetailsContacts = () => {
  const dealerPublicAddress = useSelector(getDealerPublicAddress);
  const { canEditData, form } = useDealerDetailsContext();
  const getTranslatedContactType = (contactType: DealerContactType) => {
    return t(TextCodes.Contact[contactType]);
  };
  const webPage = dealerPublicAddress?.webPage;
  const contacts = form.watch('contacts');

  const handleWebPageClick = () => {
    if (webPage) {
      const formattedWebPage =
        webPage.startsWith('http://') || webPage.startsWith('https://')
          ? webPage
          : `https://${webPage}`;
      window.open(formattedWebPage, '_blank');
    }
  };

  const handleContactClick = (e: React.MouseEvent<HTMLInputElement>) => {
    const type = e.currentTarget?.type;
    const contactValue = e.currentTarget?.value;

    if (type && contactValue) {
      if (type === 'tel') {
        window.open(`tel:${contactValue}`, '_blank');
      } else if (type === 'email') {
        window.open(`mailto:${contactValue}`, '_blank');
      }
    }
  };
  const hasContactData = webPage || contacts.some((contact) => contact.contactValue) || canEditData;

  return hasContactData ? (
    <Grid container spacing={{ xs: 2, sm: 4, md: 10 }}>
      <Grid item xs={12} sm={6}>
        <Box sx={{ mt: '40px' }}>
          {canEditData || webPage || contacts.some((contact) => contact.contactValue) ? (
            <Title variant='h4' noDot sx={{ mt: 2, mb: 2 }}>
              {t(TextCodes.Dealer.Details.Contacts)}
            </Title>
          ) : null}
          <Grid
            className='contacts-grid'
            container
            columnSpacing={{
              xs: 2,
              sm: 4,
              md: 10,
            }}
          >
            {contacts?.map(
              (contact, index) =>
                (contact.contactValue || canEditData) && (
                  <Grid xs={12} item key={contact.contactType + index}>
                    <Controller
                      name={`contacts.${index}.contactValue`}
                      control={form.control}
                      render={({ field, formState: { errors } }) => (
                        <StyledTextField
                          {...field}
                          sx={{ mt: 4 }}
                          label={getTranslatedContactType(contact.contactType)}
                          fullWidth
                          type={contact.contactType === 'MobilePhoneNumber' ? 'tel' : 'email'}
                          error={!!errors.contacts?.[index]?.contactValue}
                          helperText={
                            errors.contacts?.[index]?.contactValue
                              ? errors.contacts?.[index]?.contactValue?.message
                              : ''
                          }
                          InputProps={{
                            className: !canEditData ? 'Mui-readOnly' : undefined,
                          }}
                          inputProps={
                            !canEditData
                              ? {
                                  onClick: handleContactClick,
                                  style: { cursor: 'pointer' },
                                  readOnly: !canEditData,
                                }
                              : {
                                  pattern:
                                    contact.contactType === 'MobilePhoneNumber'
                                      ? '^\\+?[\\d\\s\\-]{1,15}$'
                                      : undefined,
                                }
                          }
                        />
                      )}
                    />{' '}
                  </Grid>
                )
            )}
            {(webPage || canEditData) && (
              <Grid item xs={12}>
                <Controller
                  name='webPage'
                  control={form.control}
                  render={({ field, formState: { errors } }) => (
                    <StyledTextField
                      {...field}
                      sx={{ mt: 4 }}
                      label={t(TextCodes.Dealer.Details.Website)}
                      fullWidth
                      error={!!errors.webPage}
                      helperText={errors.webPage ? errors.webPage.message : ''}
                      inputProps={
                        !canEditData
                          ? {
                              onClick: handleWebPageClick,
                              style: { cursor: 'pointer' },
                              readOnly: !canEditData,
                            }
                          : {
                              pattern:
                                '^(https?:\\/\\/)?(www\\.)?([A-z0-9]+(-[A-z0-9]+)*\\.)+[A-z]{2,}\\/?.*$',
                            }
                      }
                      InputProps={{ className: !canEditData ? 'Mui-readOnly' : undefined }}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={12} sm={6} />
    </Grid>
  ) : null;
};
